import { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import TopNav from './TopNav';
import { useToast } from '../Toast';

const API = process.env.REACT_APP_API_URL || '';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const CATS = {
  lg:  { label: 'Ology large event', color: '#4ade80' }, // green
  sm:  { label: 'Ology small event', color: '#a78bfa' }, // purple
  hol: { label: 'Holiday',           color: '#f87171' }, // red
  oth: { label: 'Other',             color: '#94a3b8' }, // slate
};
const HOL_COLOR = '#f87171';
const LOCATIONS = ['Midtown', 'Northside', 'Power Mill', 'Tampa', 'ALL'];

// ── Date helpers — dates are LOCAL YYYY-MM-DD strings, never parsed as UTC ──────
const pad = n => String(n).padStart(2, '0');
const ymd = (y, m, d) => `${y}-${pad(m + 1)}-${pad(d)}`;
const parseLocal = s => { const [a, b, c] = s.split('-').map(Number); return new Date(a, b - 1, c); };
const fmtLocal = dt => ymd(dt.getFullYear(), dt.getMonth(), dt.getDate());

// ── Holiday math (ported from the prototype) ────────────────────────────────────
const nth = (y, m, wd, n) => { const f = new Date(y, m, 1).getDay(); return 1 + ((wd - f + 7) % 7) + (n - 1) * 7; };
const last = (y, m, wd) => { const n = new Date(y, m + 1, 0).getDate(), l = new Date(y, m, n).getDay(); return n - ((l - wd + 7) % 7); };
function easter(y) {
  const a = y % 19, b = Math.floor(y / 100), c = y % 100, d = Math.floor(b / 4), e = b % 4,
    f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3), hh = (19 * a + b - d - g + 15) % 30,
    i = Math.floor(c / 4), k = c % 4, l = (32 + 2 * e + 2 * i - hh - k) % 7, m = Math.floor((a + 11 * hh + 22 * l) / 451),
    mo = Math.floor((hh + l - 7 * m + 114) / 31), da = ((hh + l - 7 * m + 114) % 31) + 1;
  return [mo - 1, da];
}
function holidays(y) {
  const [em, ed] = easter(y);
  return [
    [0, 1, "New Year's Day"], [0, nth(y, 0, 1, 3), 'MLK Day'], [1, 14, "Valentine's Day"], [1, nth(y, 1, 1, 3), "Presidents' Day"],
    [2, 17, "St. Patrick's Day"], [em, ed, 'Easter'], [4, nth(y, 4, 0, 2), "Mother's Day"], [4, last(y, 4, 1), 'Memorial Day'],
    [5, 19, 'Juneteenth'], [5, nth(y, 5, 0, 3), "Father's Day"], [6, 4, 'Independence Day'], [8, nth(y, 8, 1, 1), 'Labor Day'],
    [9, 31, 'Halloween'], [10, 11, 'Veterans Day'], [10, nth(y, 10, 4, 4), 'Thanksgiving'], [11, 24, 'Christmas Eve'],
    [11, 25, 'Christmas'], [11, 31, "New Year's Eve"],
  ].map(([m, d, n]) => ({ date: ymd(y, m, d), title: n }));
}

const VIEWS = [
  { key: 'all', label: 'Full year' }, { key: 'h1', label: 'Jan–Jun' },
  { key: 'h2', label: 'Jul–Dec' }, { key: 'list', label: 'List' },
];
const loadPrefs = () => { try { return JSON.parse(localStorage.getItem('ology-planner') || '{}'); } catch { return {}; } };

export default function EventPlanner({ user, canUpload }) {
  const toast = useToast();
  const prefs = loadPrefs();
  const hash = (typeof window !== 'undefined' ? window.location.hash.slice(1) : '');
  // Admins can always edit (server checkPlannerManage admin-bypasses); other roles
  // need the 'upload' grant. This mirrors how admin access works elsewhere.
  const canWrite = !!canUpload || (user?.roles || []).includes('admin');

  const [events, setEvents] = useState([]);
  const [year, setYear] = useState(prefs.year || 2027);
  const [view, setView] = useState(['all', 'h1', 'h2', 'list'].includes(hash) ? hash : (prefs.view || 'all'));
  const [showHol, setShowHol] = useState(typeof prefs.showHol === 'boolean' ? prefs.showHol : true);
  const [dragId, setDragId] = useState(null);
  const [dragOver, setDragOver] = useState(null); // date string or 'unsched'
  const [dialog, setDialog] = useState(null);      // { event, date } or null
  const listRef = useRef(null);
  const jumpTo = useRef(null);

  // Persist view prefs per browser.
  useEffect(() => {
    try { localStorage.setItem('ology-planner', JSON.stringify({ view, year, showHol })); } catch {}
    try { window.history.replaceState(null, '', '#' + view); } catch {}
  }, [view, year, showHol]);

  // ── Data: load, then refresh on focus + a 30s poll (two-monitor sync) ─────────
  const loadEvents = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/planner/events`, { credentials: 'include' });
      if (!res.ok) throw new Error('load');
      const data = await res.json();
      setEvents(Array.isArray(data) ? data.map(e => ({ ...e, date: e.date || '', end: e.date ? (e.end || '') : '', time: e.time || '' })) : []);
    } catch { /* keep prior state; transient */ }
  }, []);
  useEffect(() => { loadEvents(); }, [loadEvents]);
  useEffect(() => {
    const onFocus = () => loadEvents();
    window.addEventListener('focus', onFocus);
    const t = setInterval(loadEvents, 30000);
    return () => { window.removeEventListener('focus', onFocus); clearInterval(t); };
  }, [loadEvents]);

  // Jump to a date row after switching to List view (from a "+N more" click).
  useEffect(() => {
    if (view === 'list' && jumpTo.current) {
      const el = document.getElementById('r-' + jumpTo.current);
      if (el) el.scrollIntoView({ block: 'center' });
      jumpTo.current = null;
    }
  }, [view, events]);

  // ── day -> {hol:[], ev:[{e, first}]} for the current year ─────────────────────
  const map = useMemo(() => {
    const m = {};
    const add = (k, fn) => { m[k] = m[k] || { hol: [], ev: [] }; fn(m[k]); };
    holidays(year).forEach(x => add(x.date, o => o.hol.push(x.title)));
    events.filter(e => e.date).forEach(e => {
      let cur = parseLocal(e.date);
      // end must be its OWN Date — aliasing it to `cur` would let the loop's
      // cur.setDate() advance it too, smearing single-day events across 400 days.
      const end = parseLocal(e.end && e.end > e.date ? e.end : e.date);
      let i = 0;
      while (cur <= end && i < 400) { const k = fmtLocal(cur); add(k, o => o.ev.push({ e, first: k === e.date })); cur.setDate(cur.getDate() + 1); i++; }
    });
    return m;
  }, [events, year]);

  const unscheduled = useMemo(() => events.filter(e => !e.date), [events]);

  // ── Mutations ─────────────────────────────────────────────────────────────────
  const saveEvent = async (body, editingId) => {
    const url = editingId ? `${API}/api/planner/events/${editingId}` : `${API}/api/planner/events`;
    const res = await fetch(url, {
      method: editingId ? 'PATCH' : 'POST', credentials: 'include',
      headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body),
    });
    if (!res.ok) throw new Error((await res.json().catch(() => ({}))).message || 'Could not save.');
    await loadEvents();
  };
  const deleteEvent = async (id) => {
    const res = await fetch(`${API}/api/planner/events/${id}`, { method: 'DELETE', credentials: 'include' });
    if (!res.ok) throw new Error('Could not delete.');
    await loadEvents();
  };

  // Optimistic drag: move an event to a day (or '' = unschedule), shifting a
  // multi-day event's end by the same amount.
  const moveEvent = async (id, newDate) => {
    const ev = events.find(e => String(e.id) === String(id));
    if (!ev || ev.date === newDate) return;
    let end = '';
    if (newDate && ev.date && ev.end && ev.end > ev.date) {
      const span = Math.round((parseLocal(ev.end) - parseLocal(ev.date)) / 864e5);
      const d = parseLocal(newDate); d.setDate(d.getDate() + span); end = fmtLocal(d);
    }
    const prev = { date: ev.date, end: ev.end };
    setEvents(list => list.map(e => e.id === ev.id ? { ...e, date: newDate, end } : e)); // optimistic
    try {
      const res = await fetch(`${API}/api/planner/events/${id}`, {
        method: 'PATCH', credentials: 'include', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ date: newDate, end }),
      });
      if (!res.ok) throw new Error('move');
    } catch {
      setEvents(list => list.map(e => e.id === ev.id ? { ...e, ...prev } : e)); // rollback
      toast.error("Couldn't move that event. Try again.");
    }
  };

  const openMore = (dateKey) => { jumpTo.current = dateKey; setView('list'); };

  // ── Drag plumbing (mouse) ──────────────────────────────────────────────────────
  const onItemDragStart = (e, id) => { if (!canWrite) { e.preventDefault(); return; } setDragId(String(id)); e.dataTransfer.effectAllowed = 'move'; try { e.dataTransfer.setData('text/plain', String(id)); } catch {} };
  const onDayDragOver = (e, key) => { if (!dragId) return; e.preventDefault(); e.dataTransfer.dropEffect = 'move'; setDragOver(key); };
  const onDayDrop = (e, key) => { if (!dragId) return; e.preventDefault(); const id = dragId; setDragId(null); setDragOver(null); moveEvent(id, key); };

  const half = view !== 'all';
  const monthsToShow = view === 'h1' ? [0, 1, 2, 3, 4, 5] : view === 'h2' ? [6, 7, 8, 9, 10, 11] : [...Array(12).keys()];
  const MAX = half ? 5 : 3;
  const todayKey = fmtLocal(new Date());

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-900">
      <TopNav />
      {/* Header */}
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 px-4 sm:px-6 py-2 border-b border-gray-800">
        <h1 className="text-cream text-2xl font-bold flex items-center gap-1">
          <button onClick={() => setYear(y => y - 1)} className="text-gray-500 hover:text-cream px-1" aria-label="Previous year">‹</button>
          Ology {year}
          <button onClick={() => setYear(y => y + 1)} className="text-gray-500 hover:text-cream px-1" aria-label="Next year">›</button>
        </h1>
        <div className="flex rounded-lg overflow-hidden border border-gray-700">
          {VIEWS.map(v => (
            <button key={v.key} onClick={() => setView(v.key)}
              className={`px-3 py-1.5 text-sm border-r border-gray-700 last:border-r-0 transition ${view === v.key ? 'text-white' : 'bg-gray-800 text-gray-400 hover:text-white'}`}
              style={view === v.key ? { backgroundColor: '#F05A28' } : {}}>
              {v.label}
            </button>
          ))}
        </div>
        {canWrite && (
          <button onClick={() => setDialog({ event: null, date: year === new Date().getFullYear() ? todayKey : ymd(year, 0, 1) })}
            className="px-3 py-1.5 rounded-lg text-white text-sm font-semibold" style={{ backgroundColor: '#F05A28' }}>
            + Add event
          </button>
        )}
        <div className="flex flex-wrap gap-x-3 gap-y-1 text-xs text-gray-400">
          {Object.values(CATS).map(c => (
            <span key={c.label} className="inline-flex items-center gap-1.5">
              <i className="w-2.5 h-2.5 rounded-sm inline-block" style={{ backgroundColor: c.color }} />{c.label}
            </span>
          ))}
        </div>
      </div>

      {view === 'list' ? (
        <ListView events={events} year={year} showHol={showHol} setShowHol={setShowHol} listRef={listRef}
          canWrite={canWrite} onOpen={ev => canWrite && setDialog({ event: ev })} onDelete={deleteEvent} toast={toast} />
      ) : (
        <div className="flex-1 min-h-0 flex gap-2 p-2">
          <main className={`flex-1 min-w-0 min-h-0 grid gap-2 ${half ? 'grid-cols-3 grid-rows-2' : 'grid-cols-4 grid-rows-3'}`}
            style={{ gridTemplateColumns: `repeat(${half ? 3 : 4}, minmax(0,1fr))`, gridTemplateRows: `repeat(${half ? 2 : 3}, minmax(0,1fr))` }}>
            {monthsToShow.map(m => (
              <MonthGrid key={m} m={m} year={year} map={map} MAX={MAX} half={half} todayKey={todayKey}
                canWrite={canWrite} dragOver={dragOver}
                onItemDragStart={onItemDragStart} onDayDragOver={onDayDragOver} onDayDrop={onDayDrop}
                onDayLeave={() => setDragOver(null)}
                onEventClick={ev => canWrite && setDialog({ event: ev })}
                onEmptyClick={key => canWrite && setDialog({ event: null, date: key })}
                onMore={openMore} />
            ))}
          </main>
          <UnscheduledPanel items={unscheduled} canWrite={canWrite} dragOver={dragOver}
            onItemDragStart={onItemDragStart}
            onDragOver={e => { if (!dragId) return; e.preventDefault(); setDragOver('unsched'); }}
            onDrop={e => { if (!dragId) return; e.preventDefault(); const id = dragId; setDragId(null); setDragOver(null); moveEvent(id, ''); }}
            onOpen={ev => canWrite && setDialog({ event: ev })}
            onAdd={() => canWrite && setDialog({ event: null, date: '' })} />
        </div>
      )}

      {dialog && (
        <EventDialog dialog={dialog} onClose={() => setDialog(null)} onSave={saveEvent} onDelete={deleteEvent} toast={toast} />
      )}
    </div>
  );
}

// ── Month grid ──────────────────────────────────────────────────────────────────
function MonthGrid({ m, year, map, MAX, half, todayKey, canWrite, dragOver, onItemDragStart, onDayDragOver, onDayDrop, onDayLeave, onEventClick, onEmptyClick, onMore }) {
  const first = new Date(year, m, 1).getDay();
  const n = new Date(year, m + 1, 0).getDate();
  const built = [];
  for (let i = 0; i < 42; i++) {
    const d = i - first + 1;
    if (d < 1 || d > n) { built.push(<div key={i} className="border-r border-b border-gray-700/60 bg-gray-900/40 [&:nth-child(7n)]:border-r-0" />); continue; }
    const key = ymd(year, m, d);
    const o = map[key] || { hol: [], ev: [] };
    const wd = (first + d - 1) % 7;
    const weekend = wd === 0 || wd === 6;
    const items = [
      ...o.hol.map(t => ({ kind: 'hol', title: t })),
      ...o.ev.map(({ e }) => ({ kind: 'ev', e })),
    ];
    const shown = items.length > MAX ? items.slice(0, MAX - 1) : items;
    const moreCount = items.length - shown.length;
    const isToday = key === todayKey;
    const hasHol = o.hol.length > 0;
    const tip = [`${MONTHS[m]} ${d}`, ...o.hol, ...o.ev.map(x => x.e.title)].filter(Boolean).join('\n');
    built.push(
      <div key={i} data-date={key} title={tip}
        onClick={() => onEmptyClick(key)}
        onDragOver={e => onDayDragOver(e, key)} onDrop={e => onDayDrop(e, key)} onDragLeave={onDayLeave}
        className={`border-r border-b border-gray-700/60 [&:nth-child(7n)]:border-r-0 px-1 py-0.5 overflow-hidden flex flex-col gap-px cursor-pointer hover:bg-gray-700/40
          ${weekend ? 'bg-gray-800/40' : ''} ${dragOver === key ? 'outline outline-2 -outline-offset-2 outline-[#F05A28]' : ''}`}>
        <div data-n className={`font-medium tabular-nums flex items-baseline gap-1 ${half ? 'text-[13px]' : 'text-xs'}`}>
          <b data-n className={`px-1 rounded ${isToday ? 'text-white' : hasHol ? '' : 'text-gray-300'}`}
            style={isToday ? { backgroundColor: '#F05A28' } : hasHol ? { color: HOL_COLOR } : {}}>{d}</b>
        </div>
        {shown.map((it, idx) => it.kind === 'hol' ? (
          <div key={'h' + idx} className={`${half ? 'text-xs' : 'text-[11px]'} leading-tight truncate pl-1 border-l-2`} style={{ color: HOL_COLOR }}>{it.title}</div>
        ) : (
          <div key={it.e.id} draggable={canWrite} onDragStart={e => onItemDragStart(e, it.e.id)}
            onClick={e => { e.stopPropagation(); onEventClick(it.e); }}
            className={`${half ? 'text-xs' : 'text-[11px]'} leading-tight truncate pl-1 border-l-2 hover:underline ${it.e.cat === 'lg' ? 'font-semibold' : ''} ${canWrite ? 'cursor-grab' : 'cursor-pointer'}`}
            style={{ color: (CATS[it.e.cat] || CATS.oth).color, borderLeftWidth: it.e.cat === 'lg' ? 3 : 2 }}
            title={`${it.e.title}${it.e.time ? ' · ' + it.e.time : ''}${it.e.location ? ' · ' + it.e.location : ''}`}>{it.e.title}</div>
        ))}
        {moreCount > 0 && (
          <button onClick={e => { e.stopPropagation(); onMore(key); }}
            className={`${half ? 'text-xs' : 'text-[11px]'} text-gray-500 hover:text-gray-300 hover:underline text-left`}>+{moreCount} more</button>
        )}
      </div>
    );
  }
  return (
    <section className="bg-gray-800/40 border border-gray-700 flex flex-col min-h-0 min-w-0">
      <div className="text-center uppercase tracking-widest font-bold py-0.5 text-[#F05A28] bg-gray-800" style={{ fontSize: half ? 14 : 12 }}>{MONTHS[m]}</div>
      <div className="grid grid-cols-7 text-[10px] text-gray-500 text-center border-b border-gray-700">
        {['S','M','T','W','T','F','S'].map((w, i) => <span key={i}>{w}</span>)}
      </div>
      <div className="flex-1 min-h-0 grid grid-cols-7" style={{ gridTemplateRows: 'repeat(6, minmax(0,1fr))' }}>{built}</div>
    </section>
  );
}

// ── Unscheduled panel ───────────────────────────────────────────────────────────
function UnscheduledPanel({ items, canWrite, dragOver, onItemDragStart, onDragOver, onDrop, onOpen, onAdd }) {
  return (
    <aside onDragOver={onDragOver} onDrop={onDrop}
      className={`w-52 flex-none bg-gray-800/40 border flex flex-col min-h-0 ${dragOver === 'unsched' ? 'border-[#F05A28]' : 'border-gray-700'}`}>
      <div className="text-center uppercase tracking-widest font-bold py-0.5 text-[#F05A28] bg-gray-800 text-xs">
        Unscheduled {items.length ? `(${items.length})` : ''}
      </div>
      <div className="flex-1 min-h-0 overflow-auto p-1.5 flex flex-col gap-1">
        {items.length ? items.map(e => {
          const c = CATS[e.cat] || CATS.oth;
          const sub = [e.time, e.location].filter(Boolean).join(' · ');
          return (
            <div key={e.id} draggable={canWrite} onDragStart={ev => onItemDragStart(ev, e.id)}
              onClick={() => onOpen(e)} title="Drag onto a date"
              className={`text-xs px-2 py-1 rounded border border-gray-700 bg-gray-900/60 border-l-[3px] ${canWrite ? 'cursor-grab' : 'cursor-pointer'}`}
              style={{ borderLeftColor: c.color }}>
              <span className="text-gray-200">{e.title}</span>
              {sub && <small className="block text-gray-500 text-[11px] truncate">{sub}</small>}
            </div>
          );
        }) : (
          <div className="text-gray-500 text-xs p-2 text-center">Ideas without a date go here. Drag them onto a day, or drag a day's event here to unschedule it.</div>
        )}
      </div>
      {canWrite && <button onClick={onAdd} className="m-1.5 px-2 py-1.5 rounded-lg border border-gray-700 text-gray-300 text-xs hover:bg-gray-700/40">+ Add unscheduled</button>}
    </aside>
  );
}

// ── List view ───────────────────────────────────────────────────────────────────
function ListView({ events, year, showHol, setShowHol, listRef, canWrite, onOpen, onDelete, toast }) {
  const [armed, setArmed] = useState(null);
  const yEv = events.filter(e => e.date && (e.date.startsWith(year + '-') || (e.end && e.end.startsWith(year + '-'))));
  const und = events.filter(e => !e.date);
  let rows = [...yEv.map(e => ({ ...e, kind: 'ev' })), ...(showHol ? holidays(year).map(x => ({ ...x, kind: 'hol' })) : [])];
  rows.sort((a, b) => a.date.localeCompare(b.date) || (a.kind === 'hol' ? -1 : 1));

  const del = async (id) => {
    if (armed !== id) { setArmed(id); return; }
    setArmed(null);
    try { await onDelete(id); } catch (e) { toast.error(e.message || "Couldn't delete."); }
  };

  const groups = [];
  let curM = -1, prev = null; const seen = new Set();
  rows.forEach(r => {
    const dt = parseLocal(r.date), m = dt.getMonth();
    if (m !== curM) { curM = m; groups.push({ type: 'month', label: MONTHS[m] }); }
    const anchor = seen.has(r.date) ? null : r.date; seen.add(r.date);
    let gap = '';
    if (r.kind === 'ev') { if (prev) { const g = Math.round((dt - prev) / 864e5); gap = g === 0 ? 'Same day' : g === 1 ? 'Next day' : `${g} days later`; } prev = dt; }
    groups.push({ type: 'row', r, dt, m, anchor, gap });
  });

  const dateCell = (dt, m, r) => {
    const endTxt = r.end && r.end > r.date ? ` – ${MONTHS[parseLocal(r.end).getMonth()].slice(0, 3)} ${parseLocal(r.end).getDate()}` : '';
    return (<div className="whitespace-nowrap tabular-nums">{MONTHS[m].slice(0, 3)} {dt.getDate()} <small className="text-gray-500">{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][dt.getDay()]}</small>{endTxt && <small className="text-gray-500">{endTxt}</small>}</div>);
  };

  return (
    <div ref={listRef} className="flex-1 min-h-0 overflow-auto bg-gray-800/30 border-t border-gray-800">
      <div className="flex gap-4 items-center px-4 py-2 border-b border-gray-700 text-gray-400 sticky top-0 bg-gray-900 z-10 text-sm">
        <label className="inline-flex gap-2 items-center cursor-pointer">
          <input type="checkbox" checked={showHol} onChange={e => setShowHol(e.target.checked)} className="accent-orange-500" /> Show holidays
        </label>
        <span>{yEv.length} event{yEv.length === 1 ? '' : 's'} in {year}</span>
      </div>
      <div className="text-[#F05A28] uppercase tracking-wider font-bold px-4 pt-3 pb-1 text-sm">Unscheduled</div>
      {und.length ? und.map(r => {
        const c = CATS[r.cat] || CATS.oth; const sub = [r.time, r.location, r.notes].filter(Boolean).join(' · ');
        return (
          <div key={r.id} className="grid gap-3 items-baseline px-4 py-2 border-t border-gray-800 text-sm hover:bg-gray-700/30" style={{ gridTemplateColumns: '92px minmax(0,1fr) 130px 72px' }}>
            <div className="text-gray-500 text-xs">No date</div>
            <button onClick={() => onOpen(r)} className="text-left font-medium text-gray-200 min-w-0">{r.title}{sub && <small className="block font-normal text-gray-500 text-xs truncate">{sub}</small>}</button>
            <div className="inline-flex items-center gap-1.5 text-xs text-gray-400"><i className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />{c.label}</div>
            {canWrite && <button onClick={() => del(r.id)} className={`text-xs px-2 py-0.5 rounded border justify-self-end ${armed === r.id ? 'text-red-400 border-red-400' : 'text-gray-500 border-transparent hover:border-gray-600'}`}>{armed === r.id ? 'Confirm' : 'Delete'}</button>}
          </div>
        );
      }) : <div className="px-4 py-2 text-gray-500 text-sm">Nothing unscheduled. Leave the date blank when adding an event to park it here.</div>}
      {!yEv.length && <div className="py-10 text-center text-gray-500">No events in {year} yet. Use “+ Add event”, or click any day on the calendar.</div>}
      {groups.map((g, i) => g.type === 'month' ? (
        <div key={'m' + i} className="text-[#F05A28] uppercase tracking-wider font-bold px-4 pt-3 pb-1 text-sm">{g.label}</div>
      ) : g.r.kind === 'hol' ? (
        <div key={'r' + i} id={g.anchor ? 'r-' + g.anchor : undefined} className="grid gap-3 items-baseline px-4 py-1.5 border-t border-gray-800 text-sm" style={{ gridTemplateColumns: '92px minmax(0,1fr) 130px 72px' }}>
          {dateCell(g.dt, g.m, g.r)}
          <div style={{ color: HOL_COLOR }}>{g.r.title}</div>
          <div className="inline-flex items-center gap-1.5 text-xs text-gray-400"><i className="w-2 h-2 rounded-full" style={{ backgroundColor: HOL_COLOR }} />Holiday</div>
          <div />
        </div>
      ) : (() => {
        const c = CATS[g.r.cat] || CATS.oth; const sub = [g.r.time, g.r.location, g.r.notes].filter(Boolean).join(' · ');
        return (
          <div key={'r' + i} id={g.anchor ? 'r-' + g.anchor : undefined} className="grid gap-3 items-baseline px-4 py-1.5 border-t border-gray-800 text-sm hover:bg-gray-700/30" style={{ gridTemplateColumns: '92px minmax(0,1fr) 130px 72px' }}>
            {dateCell(g.dt, g.m, g.r)}
            <button onClick={() => onOpen(g.r)} className="text-left font-medium text-gray-200 min-w-0">{g.r.title}{sub && <small className="block font-normal text-gray-500 text-xs truncate">{sub}</small>}</button>
            <div className="inline-flex items-center gap-1.5 text-xs text-gray-400"><i className="w-2 h-2 rounded-full" style={{ backgroundColor: c.color }} />{c.label}</div>
            {canWrite ? <button onClick={() => del(g.r.id)} className={`text-xs px-2 py-0.5 rounded border justify-self-end ${armed === g.r.id ? 'text-red-400 border-red-400' : 'text-gray-500 border-transparent hover:border-gray-600'}`}>{armed === g.r.id ? 'Confirm' : 'Delete'}</button> : <div className="text-gray-600 text-xs text-right">{g.gap}</div>}
          </div>
        );
      })())}
    </div>
  );
}

// ── Add / edit dialog ───────────────────────────────────────────────────────────
function EventDialog({ dialog, onClose, onSave, onDelete, toast }) {
  const ev = dialog.event;
  const [title, setTitle] = useState(ev?.title || '');
  const [date, setDate] = useState(ev?.date || dialog.date || '');
  const [end, setEnd] = useState(ev?.end || '');
  const [cat, setCat] = useState(CATS[ev?.cat] ? ev.cat : 'sm');
  const [time, setTime] = useState(ev?.time || '');
  const [location, setLocation] = useState(LOCATIONS.includes(ev?.location) ? ev.location : '');
  const [notes, setNotes] = useState(ev?.notes || '');
  const [err, setErr] = useState('');
  const [confirm, setConfirm] = useState(false);
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const t = title.trim();
    if (!t) { setErr('Enter a name.'); return; }
    if (end && date && end < date) { setErr('End date must be on or after the start date.'); return; }
    setSaving(true);
    try {
      await onSave({ title: t, date, end: (!date || end === date) ? '' : end, cat, time: time.trim(), location, notes: notes.trim() }, ev?.id);
      onClose();
    } catch (e2) { setErr(e2.message || "Couldn't save. Try again."); }
    finally { setSaving(false); }
  };
  const doDelete = async () => {
    try { await onDelete(ev.id); onClose(); } catch { setErr("Couldn't delete. Try again."); setConfirm(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center px-4" onClick={onClose}>
      <div className="bg-gray-800 rounded-2xl border border-gray-700 w-full max-w-md" onClick={e => e.stopPropagation()}>
        <form onSubmit={submit} className="flex flex-col gap-3 p-5">
          <h2 className="text-white text-xl font-bold">{ev ? 'Edit event' : 'Add event'}</h2>
          <label className="flex flex-col gap-1 text-xs text-gray-400">Name
            <input value={title} maxLength={120} autoFocus onChange={e => { setTitle(e.target.value); setErr(''); }} placeholder="Anniversary party"
              className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </label>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs text-gray-400">Date (blank if not set)
              <input type="date" value={date} onChange={e => { setDate(e.target.value); setErr(''); }}
                className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-400">End date (optional)
              <input type="date" value={end} onChange={e => { setEnd(e.target.value); setErr(''); }}
                className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </label>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <label className="flex flex-col gap-1 text-xs text-gray-400">Category
              <select value={cat} onChange={e => setCat(e.target.value)}
                className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
                {Object.entries(CATS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1 text-xs text-gray-400">Time (optional)
              <input value={time} maxLength={40} onChange={e => setTime(e.target.value)} placeholder="6–9 pm"
                className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500" />
            </label>
          </div>
          <label className="flex flex-col gap-1 text-xs text-gray-400">Location
            <select value={location} onChange={e => setLocation(e.target.value)}
              className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500">
              <option value="">Not set</option>
              {LOCATIONS.map(l => <option key={l} value={l}>{l}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-gray-400">Notes (optional)
            <textarea value={notes} maxLength={2000} onChange={e => setNotes(e.target.value)} placeholder="Band booked, need food truck"
              className="bg-gray-700 text-white text-sm px-3 py-2 rounded-lg min-h-[64px] resize-y focus:outline-none focus:ring-2 focus:ring-orange-500" />
          </label>
          {err && <div className="text-red-400 text-xs">{err}</div>}
          {!confirm ? (
            <div className="flex gap-2 items-center flex-wrap">
              <button type="submit" disabled={saving} className="px-3 py-2 rounded-lg text-white text-sm font-semibold disabled:opacity-50" style={{ backgroundColor: '#F05A28' }}>{saving ? 'Saving…' : 'Save'}</button>
              <button type="button" onClick={onClose} className="px-3 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm">Cancel</button>
              <span className="flex-1" />
              {ev && <button type="button" onClick={() => setConfirm(true)} className="px-3 py-2 rounded-lg border border-red-500 text-red-400 text-sm">Delete</button>}
            </div>
          ) : (
            <div className="flex gap-2 items-center text-red-400 text-sm">
              Delete this event?
              <button type="button" onClick={doDelete} className="px-3 py-2 rounded-lg border border-red-500 text-red-400 text-sm">Delete</button>
              <button type="button" onClick={() => setConfirm(false)} className="px-3 py-2 rounded-lg border border-gray-600 text-gray-300 text-sm">Keep</button>
            </div>
          )}
        </form>
      </div>
    </div>
  );
}
